import Modal from "components/ui/modal";

import BetTicketBorder from "components/ui/betTicketBorder/index.jsx";
import useBetTicketBorderSvgProps from "hooks/betslip/useBetTicketBorderSvgProps.js";
import { Fragment } from "react";
import { isRacingGame, makeCurrencyText, mergeClassNames, toFixed } from "utils/common.js";
import { GAME_TYPE, GAME_TYPE_ICON_MAPPER, GAME_TYPE_MAPPER, KENO_BALL_SIZE } from "constants/game.constants.js";
import { getBetEventName } from "utils/bets.jsx";
import { useTranslation } from "react-i18next";
import runMarketUtilsFunction from "utils/markets/run.js";
import GradientButton from "components/ui/gradientButton/index.jsx";
import { navigateToPage } from "utils/navigate.js";
import Paths from "constants/path.constants.js";
import { useNavigate } from "react-router-dom";
import { renderAmount } from "utils/numbersRender.js";
import { selectSessionCurrency } from "store/slices/auth/selectors.js";
import useAppSelector from "hooks/store/useAppSelector.js";
import KenoBalls from "components/keno/bets/kenoBalls/index.jsx";
import BetBalls from "components/luckySix/betBalls/index.jsx";

const BecamePromotionModal = ({
	isVisible,
	promotionBets,
	handleOnOk,
	isMulti,
	isMobile,
	totalFactor,
	possibleWin
}) => {
	const borderSvgStyle = useBetTicketBorderSvgProps(isMobile ? "--vs--bg-color" : "--vs--bg-color-base");
	const { t } = useTranslation();
	const navigate = useNavigate();
	const currency = useAppSelector(selectSessionCurrency);

	const renderSingleBetItem = (bet, index, hidePossibleWin) => {
		const betElement = runMarketUtilsFunction(
			"makeGroupTitle",
			[{ ...bet, team1: bet.gameData?.team1?.countryCode, team2: bet.gameData?.team2?.countryCode }],
			bet.gameType
		);
		const winnerMarketText = runMarketUtilsFunction(
			"makeWinnerMarketName",
			[{ ...bet, team1: bet.gameData?.team1?.countryCode, team2: bet.gameData?.team2?.countryCode }],
			bet.gameType
		);

		return (
			<div className="vs--became-promotion-modal-content-item" key={index}>
				<div className="vs--became-promotion-modal-content-item-header">
					<Fragment>
						<i className={mergeClassNames(GAME_TYPE_ICON_MAPPER[bet.gameType], "vs--mr-4 vs--font-20")} />
						{[GAME_TYPE.KENO, GAME_TYPE.LUCKY_SIX].includes(bet.gameType) || isRacingGame(bet.gameType) ? (
							<span className="vs--became-promotion-modal-content-item-header-text">
								{t(`common.${GAME_TYPE_MAPPER[bet.gameType]}`)}
							</span>
						) : (
							<span className="vs--became-promotion-modal-content-item-header-text">{getBetEventName(bet)}</span>
						)}
					</Fragment>
				</div>
				<div className={mergeClassNames('vs--became-promotion-modal-content-item-body', hidePossibleWin ? 'short' : '' )}>
					<div className="vs--became-promotion-modal-content-item-body-text">{betElement}</div>
					<div className="vs--became-promotion-modal-content-item-body-market">
						{bet?.gameType === GAME_TYPE.KENO ? (
							<KenoBalls balls={bet.outcome.split(":").map((b) => Number(b))} size={KENO_BALL_SIZE.SMALL} />
						) : bet?.gameType === GAME_TYPE.LUCKY_SIX && bet.outcome?.includes(":") ? (
							<div className="vs--lucky-six-balls">
								<BetBalls numbers={bet.outcome.split(":").map((number) => +number)} clickable={false} border="big" />
							</div>
						) : (
							<div className="vs--became-promotion-modal-content-item-body-market-name">
								{winnerMarketText} {bet.argument}
							</div>
						)}
						<div className="vs--became-promotion-modal-content-item-body-market-value">{bet.factor}</div>
					</div>
					<BetTicketBorder svgProps={borderSvgStyle} />
					{!hidePossibleWin &&
						<div className="vs--became-promotion-modal-content-item-body-possible-win">
							<div className="vs--became-promotion-modal-content-item-body-possible-win-text">
								{t("common.doubleDooblePossibleWinAmount")}
							</div>
							<div className="vs--became-promotion-modal-content-item-body-possible-win-amount">
								{makeCurrencyText(bet.possibleWin, {
									decimalCount: currency.decimalCount,
									code: currency.code
								})}
							</div>
						</div>
					}
				</div>
			</div>
		);
	};

	const renderMultiBetItem = (bets, index) => {
		const firstBet = bets[0];
		const betElement = runMarketUtilsFunction(
			"makeGroupTitle",
			[{ ...firstBet, team1: firstBet.gameData?.team1?.countryCode, team2: firstBet.gameData?.team2?.countryCode }],
			firstBet.gameType
		);
		const winnerMarketText = runMarketUtilsFunction(
			"makeWinnerMarketName",
			[{ ...firstBet, team1: firstBet.gameData?.team1?.countryCode, team2: firstBet.gameData?.team2?.countryCode }],
			firstBet.gameType
		);

		return (
			<div className="vs--became-promotion-modal-content-item" key={index}>
				<div className="vs--became-promotion-modal-content-item-header">
					<Fragment>
						<i className={mergeClassNames("ic_bet-ticket", "vs--mr-4 vs--font-20")} />
						<span className="vs--became-promotion-modal-content-item-header-text">{`${t("bet.multi")} (${bets.length})`}</span>
					</Fragment>
				</div>
				<div className="vs--became-promotion-modal-content-item-body multi">
					<div className="vs--became-promotion-modal-content-item-body-text">{`${betElement}: ${winnerMarketText} ${bets.length > 1 ? `+${bets.length - 1}` : ""}`}</div>
					<div className="vs--became-promotion-modal-content-item-body-market">
						<div className="vs--became-promotion-modal-content-item-body-market-name">{t("bet.totalOdds")}</div>
						<div className="vs--became-promotion-modal-content-item-body-market-value">
							{toFixed(totalFactor, 2)}
						</div>
					</div>
					<BetTicketBorder svgProps={borderSvgStyle} />
				</div>
			</div>
		);
	};

	const viewBet = () => {
		navigateToPage(navigate, Paths.HISTORY);
		handleOnOk();
	};

	return (
		promotionBets.length > 0 && (
			<Modal
				isOpen={isVisible}
				onCancel={handleOnOk}
				classNames={{
					content: `vs--promotion-notification-modal became-promotion ${isMobile ? "mobile" : ""}`,
					outside: "vs--promotion-notification-modal-outside"
				}}
				onOutsideClick={isMobile ? handleOnOk : () => {}}
				closeIcon={null}
			>
				<div className="vs--became-promotion-modal">
					<div className="vs--became-promotion-modal-header">
						<div className="vs--became-promotion-modal-header-text">
							<span className="vs--became-promotion-modal-header-text-main">
								{t("common.becameDoubleDoobleParticipant")}
							</span>
							<span className="vs--became-promotion-modal-header-text-sub">
								{isMulti ? (
									<>
										<span className="vs--became-promotion-modal-header-text-sub-possible-win">
											{t("common.doubleDooblePossibleWinAmount")}:
										</span>
										<span className="vs--became-promotion-modal-header-text-sub-amount">
											{makeCurrencyText(possibleWin, {
												decimalCount: currency.decimalCount,
												code: currency.code
											})}
										</span>
									</>
								) : promotionBets.length === 1 ? (
									<>
										<span className="vs--became-promotion-modal-header-text-sub-possible-win">
											{t("common.doubleDooblePossibleWinAmount")}:
										</span>
										<span className="vs--became-promotion-modal-header-text-sub-amount">
											{makeCurrencyText(promotionBets[0].possibleWin, {
												decimalCount: currency.decimalCount,
												code: currency.code
											})}
										</span>
									</>
								) : (
									<>
										<span className="vs--became-promotion-modal-header-text-sub-possible-win">
											{t("common.participating")}:
										</span>
										<span className="vs--became-promotion-modal-header-text-sub-amount">{`${promotionBets.length} ${t("bet.bets")}`}</span>
									</>
								)}
							</span>
						</div>
						<div className="vs--became-promotion-modal-header-image" />
					</div>
					<div className="vs--became-promotion-modal-content">
						{isMulti
							? renderMultiBetItem(promotionBets)
							: promotionBets.map((item, index) => renderSingleBetItem(item, index, promotionBets.length === 1))}
					</div>
					<div className="vs--became-promotion-modal-footer">
						<GradientButton onClick={viewBet}>{t("common.viewBet")}</GradientButton>
						<GradientButton type="secondary" onClick={handleOnOk}>
							{t("common.placeMoreBets")}
						</GradientButton>
					</div>
				</div>
			</Modal>
		)
	);
};
export default BecamePromotionModal;
